import {
  Box,
  Flex,
  Icon,
  InlineNotification,
  TableIconButton,
  Td,
  Text,
  Tr,
  useTranslation,
  useToast,
  Checkbox,
} from "@familyzone/component-library";
import React, { FC, useCallback, useMemo, useState } from "react";
import { Cohort } from "../../../types/Cohorts";
import { deleteCohort, unarchiveCohort, searchCohorts } from "../../../utils/api/Cohorts";
import Link from "../../Link";
import TableBasedPage from "../../templates/TableBasedPage";
import CohortAddModal from "./CohortAddModal";
import ArchivedStatusPill from "../ArchivedStatusPill";
import { DeleteButtonNew } from "../../../modules/DeleteButtonNew";
import { IconButton } from "../../../modules/IconButton";
import { IdentitySearch, IdentitySearchParams } from "../IdentitySearch/IdentitySearch";

interface ExtraParams {
  includeArchived: boolean;
}

const CohortsTable: FC = () => {
  const { t } = useTranslation();
  const [tableResults, setTableResults] = useState<Cohort[]>([]);
  const [loading, setLoading] = useState(true);
  const [includeArchived, setIncludeArchived] = useState(false);
  const [refresh, setRefresh] = useState(false);

  const extraParams = useMemo<ExtraParams>(() => {
    return {
      includeArchived: includeArchived,
    };
  }, [includeArchived]);

  const { successToast, errorToast } = useToast();
  const PAGE_SIZE = 50;

  const breadcrumbs = [
    { title: t("Configuration"), url: "/config", isActive: false },
    { title: t("Users and Groups"), url: "/config/device/userdb", isActive: false },
    { title: t("Cohorts"), isActive: true },
  ];

  const columns = [
    { headerText: t("Name"), columnName: "name", sortable: true, searchable: true },
    { headerText: t("Description"), columnName: "description", sortable: true, searchable: true },
    { headerText: t("Status"), columnName: "status", sortable: false, searchable: false },
    { headerText: t("Type"), columnName: "type", sortable: true, searchable: false },
    { headerText: t("Operations"), columnName: "operations", sortable: false, searchable: false },
  ];

  const operations = (id: string, type: string, active: boolean): JSX.Element => {
    const isDefault = type === "DEFAULT";
    const isActive = active;

    const EditButton = () => (
      <Box mr="sp8">
        <Link to={`/config/device/userdb/cohorts/${id}`}>
          <TableIconButton aria-label="Edit" icon={<Icon icon="fa-pencil" variant="solid" color="text.paragraph.light" />} />
        </Link>
      </Box>
    );

    const DeleteButton = () => (
      <Box mr="sp8">
        <DeleteButtonNew
          onClick={() =>
            void (async () => {
              await handleDeleteCohort(id);
            })()
          }
          titleToDelete="Cohort"
          actionVerb="Archive"
          bodyToDelete="cohort"
        />
      </Box>
    );

    const UnarchiveButton = () => (
      <Box mr="sp8">
        <IconButton
          onClick={() =>
            void (async () => {
              await handleUnarchiveCohort(id);
            })()
          }
          title="Unarchive Cohort"
          actionVerb="Unarchive"
          body="Are you sure you want to unarchive this cohort"
        />
      </Box>
    );

    return (
      <Flex>
        {/* Active DEFAULT cohorts: Show Edit + Delete */}
        {isDefault && isActive && (
          <>
            <EditButton />
            <DeleteButton />
          </>
        )}

        {/* Inactive DEFAULT cohorts: Show Unarchive */}
        {isDefault && !isActive && <UnarchiveButton />}

        {/* Reserved cohorts: Show Edit only */}
        {!isDefault && <EditButton />}
      </Flex>
    );
  };

  const tableDataMap = (cohort: Cohort, index: number): JSX.Element => {
    return (
      <Tr key={index}>
        <Td data-testid="cohort-name">
          <Link to={`/config/device/userdb/cohorts/${cohort.id}`}>
            <Text fontSize={"sm"}>{cohort.name}</Text>
          </Link>
        </Td>
        <Td data-testid="cohort-description">
          <Text>{cohort.description}</Text>
        </Td>
        <Td data-testid="group-status">
          <ArchivedStatusPill archived={!cohort.active} />
        </Td>
        <Td data-testid="cohort-type">
          <Text>{cohort.type}</Text>
        </Td>
        <Td>{operations(cohort.id, cohort.type, cohort.active)}</Td>
      </Tr>
    );
  };

  const aboveTableChildren = (): JSX.Element => {
    return (
      <Flex mt="sp24" mr="sp24" justifyContent="end">
        <CohortAddModal />
        <Box ml="sp8" />
      </Flex>
    );
  };

  const helperNotification = (): JSX.Element => {
    return (
      <Box ml="sp12" minW="300px" maxH="sp12">
        <InlineNotification
          notificationDescription="Please use search to show different results."
          notificationTitle={`Showing you ${tableResults.length} cohort(s)`}
          status="info"
        />
      </Box>
    );
  };

  const search = useCallback((params: IdentitySearchParams<ExtraParams>): Promise<Cohort[]> => {
    return searchCohorts(params.query, params.pageSize, params.extra?.includeArchived);
  }, []);

  const tableChildren = (): JSX.Element => {
    return (
      <Flex direction="column" gap="sp8">
        <Flex>
          <Box width="536px">
            <IdentitySearch<Cohort, ExtraParams>
              searchFn={search}
              setLoading={setLoading}
              extraParams={extraParams}
              setState={setTableResults}
              pageSize={PAGE_SIZE}
              refresh={refresh}
              setRefresh={setRefresh}
            />
          </Box>
          {!loading && helperNotification()}
        </Flex>
        <Box mt="sp8" display="flex" alignItems="center">
          <Flex mr="sp8">
            <Checkbox
              name="includeArchived"
              isChecked={includeArchived}
              onChange={(e) => setIncludeArchived(e.target.checked)}
              aria-label="Include archived cohorts"
              data-testid="include-archived-checkbox"
            />
          </Flex>
          <label>
            <Text fontSize="m" fontWeight="medium">
              Include archived cohorts?
            </Text>
          </label>
        </Box>
      </Flex>
    );
  };

  const handleDeleteCohort = async (id: string) => {
    try {
      await deleteCohort(id);
      successToast({ title: "Success!", description: "We've received the request and it has been sent for processing." });
      setRefresh(true);
    } catch (error) {
      errorToast({ title: "Error", description: "We're not able to process the request at this time. Please try again later." });
    }
  };

  const handleUnarchiveCohort = async (id: string) => {
    try {
      await unarchiveCohort(id);
      successToast({ title: "Success!", description: "We've received the request and it has been sent for processing." });
      setRefresh(true);
    } catch (error) {
      errorToast({ title: "Error", description: "We're not able to process the request at this time. Please try again later." });
    }
  };

  return (
    <TableBasedPage
      title="Cohorts"
      loaded={!loading}
      breadcrumbs={breadcrumbs}
      columns={columns}
      data={tableResults}
      tableDataMap={tableDataMap}
      children={aboveTableChildren()}
      childrenInTableHeader={tableChildren()}
      showSearch={false}
    />
  );
};

export default CohortsTable;
